import { 
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ViewEncapsulation,
  Input,
  Output,
  EventEmitter, 
  ChangeDetectorRef,
  OnDestroy} from '@angular/core';
import { ToolEnum } from 'src/app/engine/models/tool.enum';
import { ViewToolsMode} from '../../models/view-tools-mode.enum';
import { Select, Store } from '@ngxs/store';
import { EngineState } from 'src/app/engine/state/engine.state';
import { Observable, Subject } from 'rxjs';
import { ViewService } from '../../view.service';
import { Fullscreen } from 'src/app/shared/libraries/fullscreen/fullscreen';
import { ViewState } from '../../state/view.state';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { StoryLabelsState } from 'src/app/story/state/labels/story.labels.state';
import { LabelModel } from 'src/app/story/models/labels/label.model';
import { MediaModel } from 'src/app/story/models/media.model';
import { ViewLayoutStatus } from '../../models/view-layout-status.enum';
import { ViewActions } from '../../state/view.actions';
import { LayoutService } from '../../layout.service';
import { ViewLayout } from '../../models/view-layout.enum';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-view-tools',
  templateUrl: './view-tools.component.html',
  styleUrls: ['./view-tools.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class ViewToolsComponent implements OnInit, OnDestroy {
  // ---------- Observables ----------
  @Select(EngineState.getActiveTool)
  activeTool$: Observable<ToolEnum>;

  @Select(ViewState.getPresentationActive)
  presentationActive$: Observable<boolean>;

  @Select(StoryChaptersState.getIsChapterAnimated)
  isChapterAnimated$: Observable<boolean>;

  @Select(EngineState.getShowLabels)
  showLabels$: Observable<boolean>;

  @Select(EngineState.getAnimationPlaying)
  animationPlaying$: Observable<boolean>;

  @Select(StoryLabelsState.getChapterLabels)
  labels$: Observable<LabelModel[]>;

  @Select(StoryChaptersState.getChapterCount)
  chapterCount$: Observable<number>;

  @Select(StoryChaptersState.getSelectedChapterAudio)
  selectedChapterAudio$: Observable<MediaModel>;



  // ---------- Class Variables ----------
  viewToolsMode = ViewToolsMode;
  viewLayout = ViewLayout;
  canFullscreen = true;
  isFullscreen = false;
  toolEnum = ToolEnum;
  private ngUnsubscribe = new Subject();



  // ---------- Input / Output ----------
  @Input()
  animate = true;

  @Input()
  public get mode() {
    return this._mode;
  }
  public set mode(value) {
    this._mode = value;
  }
  private _mode = ViewToolsMode.Main;
  
  @Output()
  modeChange = new EventEmitter<ViewToolsMode>();


  // ---------- Lifecycle Events ----------
  constructor(
    private viewService: ViewService,
    private layoutService: LayoutService,
    private store: Store,
    private changeDetectorRef: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.canFullscreen = Fullscreen.canFullscreen();
    this.store
      .select(ViewState.getLayout)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.changeDetectorRef.markForCheck();
      });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  handleSelectTool(tool: ToolEnum) {
    this.viewService.selectTool(tool);
  }

  handleUndoClick() {
    this.viewService.undo();
  }

  handleToggleLabelsClick() {
    this.viewService.toggleLabels();
  }


  handleFullscreenClick() {
    Fullscreen.toggleFullscreen();
    this.isFullscreen = Fullscreen.isFullscreen();
  }

  handleSearchClick() {
    this.viewService.toggleSearch();
  }

  handlePlayClick() {
    this.viewService.toggleAnimation();
  }

  handlePresentClick() {
    this.viewService.togglePresentation();
  }
  
  handleHelpClick() {
    this.viewService.toggleHelp();
  }

  handleNewTabClick() {
    this.viewService.openInNewTab();
  }

  toggleToolbar(bar: 'media' | 'tools' | 'toolbar') {
    const layout = this.store.selectSnapshot(ViewState.getLayout);
    layout.media.status = ViewLayoutStatus.Hide;
    layout.tools.status = ViewLayoutStatus.Hide;
    layout.toolbar.status = ViewLayoutStatus.Hide;
    layout[bar].status = ViewLayoutStatus.Show;
    this.store.dispatch(new ViewActions.SetLayout(layout));
  }

  isEnabled(layout: ViewLayout) {
    return this.layoutService.isLayoutEnabled(layout);
  }
}
